import { ProblemDetail } from 'src/common/ProblemDetail';

export interface ValidationRulesDto {
    minLength: number | null;
    maxLength: number | null;
    minValue: number | null;
    maxValue: number | null;
    pattern: string | null;
}

export interface FormFieldConfigDto {
    required: boolean;
    optionsMode?: OptionMode | null;
    options?: string[];
    defaultValue?: string | null;
    placeholder?: string | null;
    validation?: ValidationRulesDto | null;
    dynamicOptionsKey?: string | null;
}

export enum FormFieldType {
    TEXT = 'TEXT',
    NUMBER = 'NUMBER',
    DATE = 'DATE',
    DROPDOWN = 'DROPDOWN',
    CHECKBOX = 'CHECKBOX',
    ATTACHMENT = 'ATTACHMENT',
    LIST = 'LIST',
    COMPUTED = 'COMPUTED',
}

export enum FormItemKind {
    FIELD = 'field',
    SECTION = 'section',
}

export enum FormFieldItemType {
    TEXT = 'TEXT',
    NUMBER = 'NUMBER',
    DATE = 'DATE',
    DROPDOWN = 'DROPDOWN',
    CHECKBOX = 'CHECKBOX',
    ATTACHMENT = 'ATTACHMENT',
}

export enum OptionMode {
    STATIC = 'STATIC',
    DYNAMIC = 'DYNAMIC',
}

export enum UploadTarget {
    LOCAL = 'LOCAL',
    GOOGLE_DRIVE = 'GOOGLE_DRIVE',
    NOVA_AVA = 'NOVA_AVA',
}

export interface FormItemDto {
    kind: FormItemKind;
    /** Stable internal identifier used for React keys and drag-and-drop. Not persisted to the backend. */
    _id?: string;
    key: string;
    label: string;
}

export interface FormFieldDto extends FormItemDto {
    kind: FormItemKind.FIELD;
    type: FormFieldType;
    typeItem?: FormFieldItemType; // For LIST type, defines the type of items in the list
    config: FormFieldConfigDto;
}

export enum RepeatType {
    NON_REPEATING = 'non_repeating',
    REPEATING = 'repeating',
}

export interface RepeatConfigDto {
    type: RepeatType;
    min?: number;
    max?: number;
    initial?: number;
}

export enum LogicOperator {
    IS_EXACTLY = 'IS_EXACTLY',
    IS_NOT = 'IS_NOT',
    IS_EMPTY = 'IS_EMPTY',
    IS_NOT_EMPTY = 'IS_NOT_EMPTY',
    GT = 'GT',
    GTE = 'GTE',
    LT = 'LT',
    LTE = 'LTE',
    INCLUDES = 'INCLUDES',
}

export interface ConditionDto {
    whenPath: string;
    op: LogicOperator;
    value?: unknown;
}

export enum VisibilityMode {
    ALL = 'all',
    ANY = 'any',
}

export interface VisibilityRuleDto {
    mode: VisibilityMode;
    conditions: ConditionDto[];
}

export interface DynamicSectionDto extends FormItemDto {
    kind: FormItemKind.SECTION;
    items: FormItemDto[];
    repeat: RepeatConfigDto;
    visibleWhen?: VisibilityRuleDto | null;
}

export interface DocumentTemplateDto {
    id?: string;
    name: string;
    templateUrl: string;
    formSchema: FormItemDto[];
    createdAt?: Date;
    createdBy?: string;
    folderRegex: string;
    fileNameTemplate: string;
    uploadTarget: UploadTarget;
}

export interface SearchDto {
    page: number;
    size: number;
    sort: string;
}

export interface DocumentTemplateSearchDto extends SearchDto {}

export interface DocumentTemplateListResponseDto {
    content: DocumentTemplateDto[];
    last: boolean;
    totalElements: number;
    totalPages: number;
    first: boolean;
    size: number;
    number: number;
    empty: boolean;
}

export interface GenerateDocumentDto {
    templateId: string;
    values: FormValuesDto;
}

export interface FormValuesDto {
    [key: string]: unknown; // Dynamic form values based on the form schema
}

export interface GenerationValidationErrorDto {
    detail: string;
    parameter: string;
}

export interface GenerateDocumentValidationFailedDto extends ProblemDetail {
    errors: GenerationValidationErrorDto[];
}

export interface DocumentInstanceDto {
    id: string;
    templateId: string;
    valuesUsed: FormValuesDto;
    generatedUrl: string;
    generatedAt: string;
    generatedBy: string;
}

export interface PrefilledFormDto {
    formSchema: FormItemDto[];
    values: FormValuesDto;
    formMetadata: FormMetadataDto;
}

export interface OptionDto {
    key: string;
    label: string;
    /**
     * Whatever the data catalog entry resolves to: a scalar for a plain list, an
     * object for a record-shaped source such as `contacts`.
     */
    value: string | Record<string, unknown>;
}

export interface FormMetadataDto {
    dynamicOptionDependencies: Record<string, string[]>;
}
